import React from 'react';
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import { useEffect, useContext, useState, useRef } from "react";
import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Button,
  TableBody
} from "@mui/material";

import MUITable from "@mui/material/Table";
import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../Component/UI/Table/Table";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import classes from "./CoreAttributes.module.css";
import { DiscoveryContext } from "../discovery-context";
import { extractDomain } from "../../../utils/helpers";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import DetailedTableModal from "../../BrandAudit/BrandAuditMetricDetails";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { InfoOutlined } from "@mui/icons-material";
import { getMetricExplainations } from "../../../utils/constant";
import MUIToolTip from "../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../redux/actions/metricActions";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/auth-context";
import { useDispatch } from "react-redux";
import { formatSourceData, formatOldSourceData } from "../../../utils/metric-sources";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useBaseResponsesModal from "../../../Component/BaseResponseComponent/useBaseResponsesModal";
import BaseResponsesModal from "../../../Component/BaseResponseComponent/BaseResponsesModal";
import Modal from "../../../Component/Modal/Modal";
import { PaginatedCoreAttributesTable } from "../../Dashboard/Component/CoreAttributes/PaginatedCoreAttributesTable";

const PRINT_COLS_PER_PAGE = 4;
function filterHeaderForBrands(headEl, fullBrandOrder = [], subsetBrands = []) {
  if (!headEl || !subsetBrands.length) return headEl;
  const keepBrandIdxs = subsetBrands.map((b) => fullBrandOrder.indexOf(b)).filter((i) => i >= 0);
  const headChildren = React.Children.toArray(headEl.props.children);
  if (!headChildren.length) return headEl;
  const rowEl = headChildren[0];
  const cells = React.Children.toArray(rowEl.props.children);
  const filteredCells = cells.filter((_, idx) => {
    if (idx < 1) return true;
    return keepBrandIdxs.includes(idx - 1);
  });
  const newRow = React.cloneElement(rowEl, {}, filteredCells);
  return React.cloneElement(headEl, {}, newRow);
}
export default function CoreAttributes({ entity, isPrintingMode = false }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const abortControllerRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  const [isActionLoading, setIsActionLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const metricExplainations = getMetricExplainations();
  const customerId = user?.customer_id;
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const type = discoveryCtx.type;
  const headers = discoveryCtx.competitors.map((item) => item.name);
  const TABLE_HEADERS = [
  {
    id: "attr",
    name: ENTITY_LABELS.brandPillar,
  },
];
  const showOldSourcesRecord = discoveryCtx.state.sourceValidationStatus === "PENDING";
  const isCompanyUser = user?.user_role?.role?.name === "Company user";

  const totalBrandColumns = discoveryCtx.competitors.length;

  // Reporting mode state — mirrors MonitoringCoreAttribute
  const [showReportingModal, setShowReportingModal] = useState(false);

  // Initialize base responses modal hook
  const baseResponsesData = discoveryCtx?.core_attributes?.baseResponses || [];
  const {
    infoModalOpen,
    infoModalTitle,
    infoModalContent,
    closeInfoModal,
    handleCoreAttributeClick,
  } = useBaseResponsesModal({ baseResponses: baseResponsesData }, "core-attributes");

  // Derive isReporting from query params — same logic as MonitoringCoreAttribute
  function isReportingMode() {
    const isReporting =
      new URLSearchParams(window.location.search).get("isReporting") === "true" ||
      new URLSearchParams(window.location.search).get("reporting") === "true";
    return isReporting;
  }

  function formatName(str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const saveActions = async (actionStatement, explanation, sources, brand) => {
    setIsActionLoading(true);
    const modifiedObj = {
      title: actionStatement,
      rationale: explanation,
      brand,
      persona: "General Persona",
      geography: market == "General Market" ? "General Location" : market,
      market: "General Market",
      category,
      llm,
      isProduct: type === "product" ? true : false,
      sources,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };
    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  }

  function renderRows(rowItem, index) {
    const attName = formatName(rowItem?.name);
    return (
      <StyledTableRow key={index}>
        <Tooltip title={metricExplainations?.[attName]}>
          <StyledTableCell className={classes.attribute_headings}>
            {formatName(rowItem?.name)}
          </StyledTableCell>
        </Tooltip>
        {headers?.map((brand, idx) => {
          const attributeScore = rowItem?.scores?.find(
            (item) => item.name?.toLowerCase() === brand?.toLowerCase()
          );


          if (!attributeScore) {
            // no matching score for this brand → show "-"
            return (
              <StyledTableCell
                key={idx}
                style={{ color: "#000" }}
                className={classes.attribute_score}
              >
                -
              </StyledTableCell>
            );
          }

          // we found a matching score → render that cell
          let color = "#dc3545";
          let rating = "low";

          if (!attributeScore.score) {
            color = "#000";
          } else if (attributeScore.score >= 9) {
            rating = "high";
            color = "#198754";
          } else if (attributeScore.score >= 7) {
            rating = "fair";
            color = "#2196F3";
          }
          const attributeSources = Array.isArray(attributeScore?.sources) && attributeScore?.sources?.length > 0 ? attributeScore?.sources : attributeScore?.productSources;
          const isValidated = discoveryCtx?.core_attributes?.isValidated || false;
          const isCustomTooltip =
            (attributeScore?.name.toLowerCase() === discoveryCtx.entity.toLowerCase()) && llm?.toLowerCase().includes("search") && (showOldSourcesRecord || isValidated);
          const sources = isCustomTooltip
            ? (showOldSourcesRecord
              ? formatOldSourceData(attributeSources, llm)
              : formatSourceData(attributeSources, llm))
            : {};

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              metric: ENTITY_LABELS.brandPillar,
              attribute: attName,
              metricScore: Number(attributeScore.score).toFixed(0),
              selectedInput: {
                llm,
                market,
                category,
                brand: attributeScore.name,
                product: attributeScore.name,
              },
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              isActionLoading: isActionLoading,
              title: attributeScore.explanation,
              arrow: true,
              isDiscovery: true,
              isBrandAudit: true,
              sources,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              }
            }
            : {
              title: attributeScore.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -18],
                      },
                    },
                  ],
                },
              },
            };

          return attributeScore.score ? (
            <TooltipComponent {...tooltipProps}>
              <StyledTableCell
                style={{ color }}
                className={classes.attribute_score}
              >
                {Number(attributeScore.score).toFixed(0)}
              </StyledTableCell>
            </TooltipComponent>
          ) : (
            <StyledTableCell
              key={idx}
              style={{ color }}
              className={classes.attribute_score}
            >
              -
            </StyledTableCell>
          );
        })}

      </StyledTableRow>
    );
  }

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(8)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "80%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }


  function renderHeaders() {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors.map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index !== 0) {
              ctxCompetitor = discoveryCtx.competitors[index - 1];
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={index == 0 ? classes.table_head_cell_index : classes.table_head_cell}>
                {/* <Tooltip title={header.name} placement="top"> */}
                <div className={classes.table_head_content}>
                  {index !== 0 ? (
                    <>
                      {/* Logo with Info Icon overlay */}
                      <div className={classes.logo_container}>
                        <BrandAvatar
                          domainName={domain}
                          brandName={header.name}
                        />

                        {!isCompanyUser && baseResponsesData?.length > 0 && (
                          <div className={classes.info_icon_wrapper}>
                            <Tooltip title="View Base Prompt" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleCoreAttributeClick(header.name, baseResponsesData)}
                                sx={{
                                  padding: '1px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  color: 'rgba(0, 0, 0, 0.54)',
                                  border: '1px solid rgba(0,0,0,0.08)',
                                  '&:hover': {
                                    backgroundColor: '#2196F3',
                                    color: '#fff',
                                    border: '1px solid #2196F3',
                                  },
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: '13px' }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      {/* Brand Name below logo */}
                      <div className={classes.table_head_text}>
                        {header.name}
                      </div>
                    </>
                  ) : (
                    // First column - "Brand Pillars"
                    <div className={classes.table_head_text_comp}>
                      <div className={classes.table_head_title}>
                        <span>{header.name}</span>
                        {!showModal && (
                            <MUIIconButton onClick={() => setShowModal(true)} data-testid="brand-pillars-info-btn">
                            <InfoOutlined />
                          </MUIIconButton>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* </Tooltip> */}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  // Transform Discovery competitors into the shape PaginatedCoreAttributesTable expects:
  // { id, name } — same as extractUniqueBrands output in Monitoring
  function getUniqueBrandsForPagination(data) {
    return discoveryCtx.competitors.map((comp, index) => ({
      id: index + 1,
      name: comp.name,
    }));
  }

  let coreAttributesData = [];
  let loading = true;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus;

  if (
    discoveryCtx?.core_attributes &&
    Object.keys(discoveryCtx?.core_attributes).length > 0 &&
    discoveryCtx?.core_attributes?.summary
  ) {
    coreAttributesData = Object.keys(
      discoveryCtx.core_attributes.category_averages
    ).map((item) => {
      const scores = discoveryCtx.core_attributes.products.map((p) => ({
        name: p.brandName || p.name,
        score: p.attributes[item]?.score,
        explanation: p.attributes[item]?.explanation,
        sources: p.attributes[item]?.sources,
        productSources: p.sources
      }));
      return {
        name: item,
        scores,
      };
    });
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }


  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {coreAttributesData &&
        coreAttributesData.length > 0 &&
        !loading &&
        !hasError && (
          <>
            {/* Report View button — shown only in reporting mode, mirrors MonitoringCoreAttribute */}
            {isReportingMode() && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowReportingModal(true)}
                  style={{ textTransform: "none" }}
                >
                  {ENTITY_LABELS.brandPillar} Report View
                </Button>
              </div>
            )}

            {/* Always-visible scroll table — UI unchanged */}
            <div className="discovery-ui-table">
              <div className={classes.table_scroll_wrapper}>
                <div
                  style={{
                    minWidth: `calc(200px + ${totalBrandColumns} * 130px)`,
                  }}
                >
                  <Table
                    elevation={0}
                    data={coreAttributesData}
                    renderHeaders={() => renderHeaders(coreAttributesData)}
                    render={renderRows}
                  />
                </div>
              </div>
            </div>

            <div className="discovery-print-table" style={{ display: "none" }}>
              {Array.from({ length: Math.ceil(headers.length / PRINT_COLS_PER_PAGE) }).map((_, pageNum) => {
                const colStart = pageNum * PRINT_COLS_PER_PAGE;
                const colEnd = Math.min(headers.length, colStart + PRINT_COLS_PER_PAGE);
                const pageBrands = headers.slice(colStart, colEnd);
                const filteredHdr = filterHeaderForBrands(renderHeaders(), headers || [], pageBrands);
                const isLast = colEnd >= headers.length;
                return (
                  <div
                    key={pageNum}
                    style={{ marginBottom: "16px" }}
                  >
                    <MUITable>
                      {filteredHdr}
                      <TableBody>
                        {coreAttributesData.map((rowItem, rIdx) => {
                          const attName = formatName(rowItem?.name);
                          return (
                            <StyledTableRow key={rIdx}>
                              <StyledTableCell className={classes.attribute_headings}>
                                {formatName(rowItem?.name)}
                              </StyledTableCell>
                              {pageBrands.map((brand, idx) => {
                                const attributeScore = rowItem?.scores?.find(
                                  (item) => item.name?.toLowerCase() === brand?.toLowerCase()
                                );
                                if (!attributeScore) {
                                  return (
                                    <StyledTableCell key={idx} style={{ color: "#000" }} className={classes.attribute_score}>-</StyledTableCell>
                                  );
                                }
                                let color = "#dc3545";
                                if (attributeScore.score >= 9) color = "#198754";
                                else if (attributeScore.score >= 7) color = "#2196F3";
                                return (
                                  <StyledTableCell key={idx} style={{ color }} className={classes.attribute_score}>
                                    {attributeScore.score ? Number(attributeScore.score).toFixed(0) : "-"}
                                  </StyledTableCell>
                                );
                              })}
                            </StyledTableRow>
                          );
                        })}
                      </TableBody>
                    </MUITable>
                  </div>
                );
              })}
            </div>



            <DetailedTableModal
              show={showModal}
              onClose={() => setShowModal(false)}
              data={coreAttributesData}
              headers={headers}
              renderHeaders={renderHeaders}
              formatName={formatName}
              classes={classes}
              pdfHeight={400}
              pdfTitle={`${entity}-${ENTITY_LABELS.brandPillar}`}
            />

            {/* Add Base Responses Modal */}
            <BaseResponsesModal
              open={infoModalOpen}
              title={infoModalTitle}
              content={infoModalContent}
              onClose={closeInfoModal}
            />

            {/* Reporting Modal — mirrors MonitoringCoreAttribute */}
            <Modal
              handleClose={() => setShowReportingModal(false)}
              open={showReportingModal}
              title={`${ENTITY_LABELS.brandPillar} - Report View (Paginated for PPT)`}
              width={1200}
              sx={{
                maxWidth: "90vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
              onClose={() => setShowReportingModal(false)}
            >
              <div style={{
                maxHeight: "70vh",
                overflowY: "auto",
                overflowX: "auto",
                padding: "8px",
                width: "100%"
              }}>
                <PaginatedCoreAttributesTable
                  data={coreAttributesData}
                  uniqueBrands={getUniqueBrandsForPagination(coreAttributesData)}
                  brandDomains={
                    // Build a domain map { brandName: domainUrl } from discoveryCtx.competitors
                    discoveryCtx.competitors.reduce((acc, comp) => {
                      acc[comp.name] = comp.domain || "";
                      return acc;
                    }, {})
                  }
                  styleClasses={classes}
                  formatName={formatName}
                  isChecked={false}
                  maxBrandsPerTable={6}
                />
              </div>
            </Modal>
          </>
        )}
      {loading && !hasError && <LoadingSkeletion />}
      {hasError && (
        <div className={classes.error}>
          <ErrorView
            title={`Error Fetching ${ENTITY_LABELS.brandPillar}`}
            message={"Request Failed"}
          />
        </div>
      )}
    </MUICard>
  );
}